import React, { useState } from 'react';
import {
  Box,
  Text,
  Badge,
  Image,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { FaDollarSign } from 'react-icons/fa';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UserService from '../../utils/UserService';
import { setCurrentDealFlow as setCurrentDealFlowStore } from '../../redux-toolkit/reducers/dealFlowSlice';

const MAX_LINES = 2;

const PinnedAdverts = ({ adverts = [], maxCount = 2, pinnedDeals = [] }) => {
  const [dismissedDeals, setDismissedDeals] = useState([]);
  const [dismissedAdverts, setDismissedAdverts] = useState([]);
  const [expandedAdverts, setExpandedAdverts] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );

  // Responsive values
  const containerPadding = useBreakpointValue({ base: 2, md: 3, lg: 4 });
  const dealPadding = useBreakpointValue({ base: 3, md: 4, lg: 5 });
  const advertPadding = useBreakpointValue({ base: 2, md: 3 });
  const fontSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const titleFontSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const metricsFontSize = useBreakpointValue({ base: 'lg', md: 'xl' });
  const buttonSize = useBreakpointValue({ base: 'md', md: 'lg' });
  const avatarSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const iconButtonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const maxHeight = useBreakpointValue({ base: '300px', md: '400px' });

  const visiblePinnedDeals =
    accountType === 'investor'
      ? pinnedDeals
          .filter((deal) => !dismissedDeals.includes(deal.id))
          .slice(0, 1)
      : [];

  const visibleAdverts = adverts
    .filter((ad) => !dismissedAdverts.includes(ad.id))
    .slice(0, maxCount);

  if (visiblePinnedDeals.length === 0 && visibleAdverts.length === 0)
    return null;

  const handleSubmitTermsheet = (deal) => {
    dispatch(setCurrentDealFlowStore(deal));
    UserService.setCurrentDealFlow(deal);

    if (deal?.status === 'signed' || deal?.status === 'closed') {
      navigate('../view-post-agreement-stage');
    } else {
      navigate('../view-term-sheet');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box
      className="w-full sticky top-0 z-20"
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      mb={4}
      p={containerPadding}
      maxW={{ base: '100%', md: '2xl' }}
      mx="auto"
      style={{ position: 'sticky', top: 0 }}
    >
      <Text fontWeight="bold" mb={2}>
        <Badge colorScheme="yellow" mr={2}>
          Pinned
        </Badge>
        Featured
      </Text>
      <Box
        display="flex"
        flexDirection="column"
        gap={{ base: 3, md: 4 }}
        maxH={maxHeight}
        overflowY="auto"
        className="hide-scroll"
      >
        {/* Render Pinned Deals First (Priority) */}
        {accountType === 'investor' &&
          visiblePinnedDeals.map((deal) => {
            const { company, user } = deal;

            return (
              <Box
                key={deal.id}
                p={dealPadding}
                borderWidth="1px"
                borderRadius="xl"
                bg="white"
                borderColor="gray.200"
                position="relative"
                boxShadow="sm"
                _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
                transition="all 0.3s ease"
              >
                <Tooltip
                  label="Dismiss"
                  aria-label="Dismiss tooltip"
                  placement="top"
                >
                  <IconButton
                    aria-label="Dismiss"
                    icon={<CloseIcon />}
                    size={iconButtonSize}
                    position="absolute"
                    top={{ base: 2, md: 3 }}
                    right={{ base: 2, md: 3 }}
                    onClick={() =>
                      setDismissedDeals((prev) => [...prev, deal.id])
                    }
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: 'gray.600', bg: 'gray.100' }}
                    zIndex={1}
                  />
                </Tooltip>

                {/* Header with Company and Founder */}
                <Flex alignItems="center" mb={{ base: 3, md: 4 }}>
                  <Box flex={1}>
                    <Text
                      fontSize={titleFontSize}
                      fontWeight="600"
                      color="gray.800"
                    >
                      {company.title}
                    </Text>
                    <Flex
                      gap={2}
                      flexDirection={{ base: 'column', sm: 'row' }}
                      alignItems={{ base: 'flex-start', sm: 'center' }}
                    >
                      <Text fontSize="sm" color="gray.500">
                        {deal.stage} Round
                      </Text>
                      <Badge colorScheme="green" size="sm" borderRadius="full">
                        Raising Funds
                      </Badge>
                    </Flex>
                  </Box>
                </Flex>

                {/* Founder Info */}
                <Flex
                  alignItems="center"
                  mb={{ base: 3, md: 4 }}
                  p={{ base: 2, md: 3 }}
                  bg="gray.50"
                  borderRadius="lg"
                  flexDirection={{ base: 'column', sm: 'row' }}
                  textAlign={{ base: 'center', sm: 'left' }}
                >
                  <Avatar
                    size={avatarSize}
                    src={user.profile_image}
                    name={`${user.first_name} ${user.last_name}`}
                    mr={{ base: 0, sm: 3 }}
                    mb={{ base: 2, sm: 0 }}
                  />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                      {user.first_name} {user.last_name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Founder • Raising {formatCurrency(deal.target_amount)}
                    </Text>
                  </Box>
                </Flex>

                {/* Fundraising Status Banner */}
                <Box
                  bg="green.50"
                  border="1px solid"
                  borderColor="green.200"
                  borderRadius="lg"
                  p={{ base: 2, md: 3 }}
                  mb={{ base: 3, md: 4 }}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    flexDirection={{ base: 'column', sm: 'row' }}
                    textAlign={{ base: 'center', sm: 'left' }}
                  >
                    <FaDollarSign color="#38A169" size={16} />
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="green.700"
                      ml={{ base: 0, sm: 2 }}
                      mt={{ base: 1, sm: 0 }}
                    >
                      {user.first_name} is raising{' '}
                      {formatCurrency(deal.target_amount)} in {deal.stage}{' '}
                      funding
                    </Text>
                  </Flex>
                </Box>

                {/* Deal Metrics */}
                <Box
                  bg="gray.50"
                  p={{ base: 3, md: 4 }}
                  borderRadius="lg"
                  mb={{ base: 3, md: 4 }}
                >
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    flexDirection={{ base: 'column', md: 'row' }}
                    gap={{ base: 3, md: 0 }}
                  >
                    <Box textAlign="center" flex={1} w="100%">
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        fontWeight="500"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Funding Target
                      </Text>
                      <Text
                        fontSize={metricsFontSize}
                        fontWeight="700"
                        color="gray.800"
                      >
                        {formatCurrency(deal.target_amount)}
                      </Text>
                    </Box>

                    <Box
                      w={{ base: '100%', md: '1px' }}
                      h={{ base: '1px', md: '40px' }}
                      bg="gray.300"
                      mx={{ base: 0, md: 2 }}
                      my={{ base: 2, md: 0 }}
                    />

                    <Box textAlign="center" flex={1} w="100%">
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        fontWeight="500"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Min Investment
                      </Text>
                      <Text
                        fontSize={metricsFontSize}
                        fontWeight="700"
                        color="gray.800"
                      >
                        {formatCurrency(deal.min_ticket_size)}
                      </Text>
                    </Box>

                    <Box
                      w={{ base: '100%', md: '1px' }}
                      h={{ base: '1px', md: '40px' }}
                      bg="gray.300"
                      mx={{ base: 0, md: 2 }}
                      my={{ base: 2, md: 0 }}
                    />

                    <Box textAlign="center" flex={1} w="100%">
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        fontWeight="500"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        Closes
                      </Text>
                      <Text
                        fontSize={metricsFontSize}
                        fontWeight="700"
                        color="gray.800"
                      >
                        {moment(deal.close_date).format('MMM DD')}
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                {/* Call to Action */}
                <Button
                  w="100%"
                  size={buttonSize}
                  bg="#134A70"
                  color="white"
                  borderRadius="lg"
                  fontWeight="600"
                  _hover={{
                    bg: '#0f3a5a',
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  _active={{ bg: '#0a2a40' }}
                  onClick={() => handleSubmitTermsheet(deal)}
                  transition="all 0.2s ease"
                  leftIcon={<FaDollarSign />}
                >
                  Submit Termsheet
                </Button>
              </Box>
            );
          })}

        {/* Render Regular Adverts */}
        {visibleAdverts.map((ad) => {
          const isLong = ad.content && ad.content.length > 180;
          const isExpanded = expandedAdverts[ad.id];

          return (
            <Box
              key={ad.id}
              p={advertPadding}
              borderWidth={1}
              borderRadius="md"
              bg="gray.50"
              position="relative"
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <Tooltip
                label="Dismiss"
                aria-label="Dismiss tooltip"
                placement="top"
              >
                <IconButton
                  aria-label="Dismiss"
                  icon={<CloseIcon />}
                  size="xs"
                  position="absolute"
                  top={2}
                  right={2}
                  onClick={() =>
                    setDismissedAdverts((prev) => [...prev, ad.id])
                  }
                  variant="ghost"
                />
              </Tooltip>

              {ad.content && (
                <Box w="100%" mt={{ base: 3, md: 4 }}>
                  {ad.media && isLong && !isExpanded ? (
                    <>
                      <Text
                        fontSize={fontSize}
                        color="gray.700"
                        noOfLines={MAX_LINES}
                        mb={1}
                      >
                        {ad.content}
                      </Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        onClick={() => {
                          setExpandedAdverts((prev) => ({
                            ...prev,
                            [ad.id]: true,
                          }));
                        }}
                        p={0}
                        h="auto"
                        minW={0}
                      >
                        See More
                      </Button>
                    </>
                  ) : (
                    <Text fontSize={fontSize} color="gray.700">
                      {ad.content}
                    </Text>
                  )}
                </Box>
              )}

              {ad?.media &&
                ad?.media != 'https://s3.aws.com/hub8020/avatar.png' && (
                  <Image
                    src={ad.media}
                    alt={ad.content ? ad.content.slice(0, 30) : 'Pinned advert'}
                    borderRadius="md"
                    maxH={{ base: '150px', md: '200px' }}
                    objectFit="cover"
                    w="100%"
                    mb={ad.content ? { base: 1, md: 2 } : 0}
                    mt={!ad?.content ? { base: 4, md: 6 } : { base: 3, md: 4 }}
                  />
                )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PinnedAdverts;
